import { PropsWithChildren, useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { observer } from 'mobx-react-lite';
import { useGlobalStore, useUserStore } from 'src/context';
import { TRoleId } from 'src/lib/models';
import { toastMessages } from '../lib/toast';
import { AuthErrorPage } from './errors';

export const RequireAuth: React.FC<RequireAuthProps> = observer(({ children, ...props }) => {
  const { allowedRoles, redirectUrl, isUserRoute } = props;

  const userStore = useUserStore();
  const location = useLocation();
  const globalStore = useGlobalStore();
  const params = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isValid, setIsValid] = useState(true);
  const [isUnauthorized, setIsUnauthorized] = useState(false);

  const handler = (() => {
    if ((isUnauthorized || !isValid) && redirectUrl) return <Navigate to={redirectUrl} replace />;
    if (isUnauthorized) {
      return <AuthErrorPage />;
    } else if (!isValid) {
      globalStore.setPreviousPath(`${location.pathname}${location.search}${location.hash}`);
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
  })();

  useEffect(() => {
    setIsLoading(true);
    setIsValid(false);
    setIsUnauthorized(false);
    if (!userStore.token) {
      setIsValid(false);
      userStore.logout();
      toast.error(toastMessages.sessionExpired.success);
      globalStore.setPreviousPath(`${location.pathname}${location.search}${location.hash}`);
    } else {
      const isSameUser = userStore.user && params.id && userStore.user.id === parseInt(params.id);
      const isAllowed = userStore.user && allowedRoles?.includes(userStore.user.role.id);
      const clientIdParam = params.client_id !== undefined ? parseInt(params.client_id) : undefined;
      const hasClientAccess =
        clientIdParam === undefined ||
        !userStore.isExternalUser ||
        (!Number.isNaN(clientIdParam) && !!userStore.user?.clients?.some((client) => client.id === clientIdParam));

      if (!hasClientAccess) {
        setIsUnauthorized(true);
      } else if (isUserRoute && (isSameUser || isAllowed)) {
        setIsValid(true);
      } else if (!!allowedRoles?.length && !isAllowed) {
        setIsUnauthorized(true);
      } else {
        setIsValid(true);
      }

      setIsLoading(false);
    }
  }, [userStore.token, userStore.user, location.pathname, params.id, params.client_id]);

  return handler ?? <>{!isLoading && isValid && children}</>;
});

export type RequireAuthProps = PropsWithChildren & {
  allowedRoles?: TRoleId[];
  redirectUrl?: string;
  isUserRoute?: boolean;
};
